import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sequential progress messages for enhanced UX
const PROGRESS_STAGES = [
  { message: 'Analyzing your credit profile...', subtext: 'Reading your report' },
  { message: 'Reading your accounts...', subtext: 'Identifying credit lines' },
  { message: 'Calculating utilization...', subtext: 'Analyzing balances vs limits' },
  { message: 'Identifying score blockers...', subtext: 'Finding opportunities' },
  { message: 'Building your personalized plan...', subtext: 'Creating action steps' },
  { message: 'Optimizing your timeline...', subtext: 'Almost ready!' },
];

const ProcessingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState('generating');
  const [errorMessage, setErrorMessage] = useState('');
  const hasStartedRef = React.useRef(false);
  const [progressIndex, setProgressIndex] = useState(0);
  const [microResult, setMicroResult] = useState(null);
  
  // Get state from navigation or localStorage
  const stateSessionId = location.state?.sessionId;
  const stateTimeline = location.state?.timeline;
  const stateGoalScore = location.state?.goalScore;
  const stateMonthlyBudget = location.state?.monthlyBudget;
  const stateSkipPayment = location.state?.skipPayment;
  const stateBetaCode = location.state?.betaCode;
  const reportSource = location.state?.reportSource || localStorage.getItem('reportSource') || 'unknown';
  
  // Fallback to localStorage with proper null checks
  const sessionId = stateSessionId || localStorage.getItem('creditSessionId');
  const timeline = stateTimeline || localStorage.getItem('selectedTimeline') || 6;
  const goalScore = stateGoalScore || localStorage.getItem('selectedGoalScore') || 720;
  const monthlyBudget = stateMonthlyBudget || localStorage.getItem('monthlyBudget') || 300;
  
  // Get beta code - prefer state, then localStorage
  const betaCode = stateBetaCode || localStorage.getItem('betaCode') || null;
  const hasPaid = localStorage.getItem('hasPaid') === 'true';
  
  // Only skip payment if we have a valid beta code or user has paid
  const skipPayment = stateSkipPayment || hasPaid || (betaCode && betaCode.length > 0);

  // Progress animation
  useEffect(() => {
    if (status === 'generating') {
      const interval = setInterval(() => {
        setProgressIndex(prev => {
          if (prev < PROGRESS_STAGES.length - 1) {
            return prev + 1;
          }
          return prev;
        });
      }, 2500);
      return () => clearInterval(interval);
    }
  }, [status]);

  useEffect(() => {
    if (hasStartedRef.current) return;
    
    if (!sessionId) {
      console.error('No sessionId found - cannot proceed');
      setStatus('error');
      setErrorMessage('Session expired. Please start over from the beginning.');
      return;
    }
    
    hasStartedRef.current = true;
    
    // Store ALL session data immediately to prevent loss
    localStorage.setItem('creditSessionId', sessionId);
    localStorage.setItem('selectedTimeline', timeline);
    localStorage.setItem('selectedGoalScore', goalScore);
    localStorage.setItem('monthlyBudget', monthlyBudget);
    if (betaCode) {
      localStorage.setItem('betaCode', betaCode);
    }

    const generatePlan = async () => {
      try {
        setStatus('generating');
        
        // Allow progress animation to show
        await new Promise(resolve => setTimeout(resolve, 1500));

        const userName = localStorage.getItem('userName') || '';

        const response = await axios.post(`${API}/generate-plan`, {
          session_id: sessionId,
          timeline_months: parseInt(timeline) || 6,
          goal_score: parseInt(goalScore) || 720,
          monthly_budget: parseInt(monthlyBudget) || 300,
          user_name: userName,
          report_source: reportSource,
          credit_score: null,
          num_accounts: null,
          has_late_payments: null,
          utilization_level: null
        });

        console.log('Plan generated:', response.data);

        // IMPORTANT: Only show validation error if explicitly returned
        // Backend no longer blocks - always proceeds with available data
        if (response.data.status === 'error' && response.data.action === 'retry_upload') {
          // This should rarely happen now - backend generates plan with any data
          console.log('Backend returned retry_upload - showing error');
          setStatus('validation_error');
          setErrorMessage(response.data.message || 'There was an issue processing your report.');
          return;
        }

        // Check if we have valid plan data
        const planData = response.data;
        if (!planData || (!planData.plan_id && !planData.session_id && !planData.actions && !planData.one_thing_today)) {
          console.error('Invalid plan data received:', planData);
          // Still try to proceed - don't block the user
        }

        // Extract micro result for display
        const opportunityCount = planData.accounts_extracted?.length || planData.actions?.length || planData.accounts?.length || 3;
        const currentScore = planData.current_score || 580;
        const targetScore = planData.goal_score || goalScore;
        const pointsAway = targetScore - currentScore;
        
        // Check data quality from backend
        const dataQuality = planData.data_quality || 'medium';
        const suggestions = planData.suggestions || [];
        
        setMicroResult({
          opportunities: opportunityCount,
          pointsAway: pointsAway > 0 ? pointsAway : null,
          hasWarning: dataQuality === 'low' || suggestions.length > 0,
          analysisMessage: planData.analysis_message || 'Your personalized action plan is ready.'
        });
        
        setStatus('complete');
        
        // Brief pause to show micro result
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Route based on payment status
        if (skipPayment || betaCode) {
          setStatus('unlocking');
          
          if (betaCode) {
            try {
              await axios.post(`${API}/beta-unlock`, {
                session_id: sessionId,
                beta_code: betaCode
              });
            } catch (e) {
              console.error('Beta unlock error:', e.response?.data || e);
            }
          }
          
          localStorage.setItem('creditSessionId', sessionId);
          navigate('/upsell', { state: { sessionId, fromBeta: !!betaCode, planData } });
        } else {
          // Show locked preview for conversion
          navigate('/preview', { state: { sessionId, planData, reportSource } });
        }
      } catch (error) {
        console.error('Plan generation error:', error.response?.data || error);
        
        const errorData = error.response?.data;
        
        // Only show validation error if explicitly returned by backend
        if (errorData?.status === 'error' && errorData?.action === 'retry_upload') {
          setStatus('validation_error');
          setErrorMessage(errorData.message || 'There was an issue processing your report.');
          return;
        }
        
        // For other errors, show general error and offer retry
        const errorDetail = error.response?.data?.detail || error.message || 'Connection error';
        console.log('Error detail:', errorDetail);
        
        // Don't auto-show validation_error for generic failures
        // Instead, try to retry automatically
        setStatus('retrying');
        setProgressIndex(0);
        
        // Auto-retry once
        setTimeout(async () => {
          try {
            setStatus('retrying');
            setProgressIndex(0);
            const userName = localStorage.getItem('userName') || '';
            
            const retryResponse = await axios.post(`${API}/generate-plan`, {
              session_id: sessionId,
              timeline_months: parseInt(timeline) || 6,
              goal_score: parseInt(goalScore) || 720,
              monthly_budget: parseInt(monthlyBudget) || 300,
              user_name: userName,
              report_source: reportSource,
              credit_score: null,
              num_accounts: null,
              has_late_payments: null,
              utilization_level: null
            });
            
            if (skipPayment || betaCode) {
              if (betaCode) {
                try {
                  await axios.post(`${API}/beta-unlock`, {
                    session_id: sessionId,
                    beta_code: betaCode
                  });
                } catch (e) {
                  console.error('Beta unlock error on retry:', e);
                }
              }
              localStorage.setItem('creditSessionId', sessionId);
              navigate('/upsell', { state: { sessionId, fromBeta: !!betaCode, planData: retryResponse.data } });
            } else {
              navigate('/preview', { state: { sessionId, planData: retryResponse.data, reportSource } });
            }
          } catch (retryError) {
            console.error('Retry also failed - using client fallback:', retryError);
            // NEVER show error screen. Build a client-side fallback plan and navigate forward.
            const userName = localStorage.getItem('userName') || 'there';
            const fallbackPlan = {
              plan_id: `fallback-${Date.now()}`,
              session_id: sessionId,
              user_name: userName,
              current_score: 620,
              goal_score: parseInt(goalScore) || 720,
              timeline_months: parseInt(timeline) || 6,
              monthly_budget: parseInt(monthlyBudget) || 300,
              is_fallback_plan: true,
              data_quality: 'low',
              analysis_message: 'We prepared a general action plan. Upload your report again for personalized recommendations.',
              coach_greeting: `${userName}, here is your credit improvement strategy.`,
              one_thing_today: {
                action: `${userName}, pay down your highest utilization card today`,
                why: 'High utilization cards cost you the most points. Every dollar here has 3x the score impact.',
                score_impact: '+15 to +30 points',
                from_to: 'High% → Lower%',
                coach_message: `${userName}, this is your highest-impact move right now.`
              },
              smart_payment_plan: {
                total_budget: parseInt(monthlyBudget) || 300,
                intro: `${userName}, here is how to allocate your budget for maximum impact:`,
                allocation: [
                  { account: 'Highest Utilization Card', pay_amount: Math.round((parseInt(monthlyBudget) || 300) * 0.6), current_utilization: 70, new_utilization: 50, why_this_first: 'Cards above 50% utilization are heavily penalized.', score_unlock: '+12 to +20 points' },
                  { account: 'Second Highest Card', pay_amount: Math.round((parseInt(monthlyBudget) || 300) * 0.3), current_utilization: 45, new_utilization: 35, why_this_first: 'Getting under 30% unlocks the next scoring tier.', score_unlock: '+5 to +10 points' }
                ],
                total_score_unlock: '+17 to +30 points this month'
              },
              actions: [
                { step_number: 1, command: `${userName}, PAY DOWN YOUR HIGHEST UTILIZATION CARD`, account: { name: 'Highest Utilization Card', last4: 'XXXX', current_utilization: 70, target_utilization: 50 }, why_this_account_first: 'Highest utilization = biggest score drag.', coach_message: `${userName}, this is your #1 priority.`, score_simulation: { estimated_gain: '+12 to +20 points', reason: 'Crossing utilization threshold' } },
                { step_number: 2, command: `${userName}, RESOLVE ANY NEGATIVE ITEMS`, account: { name: 'Collections/Negatives', last4: 'N/A' }, why_this_account_first: 'Negative items suppress your score ceiling.', coach_message: `${userName}, clearing these removes a scoring cap.`, score_simulation: { estimated_gain: '+10 to +25 points', reason: 'Removing derogatory marks' } },
                { step_number: 3, command: `${userName}, MAINTAIN ON-TIME PAYMENTS`, account: { name: 'All Accounts', last4: 'ALL' }, why_this_account_first: 'Payment history is 35% of your FICO score.', coach_message: `${userName}, consistency is key.`, score_simulation: { estimated_gain: '+5 to +15 points', reason: 'Building positive payment history' } }
              ],
              score_projection: { current: 620, after_month_1: 640, after_month_3: 670, after_month_6: Math.min(700, parseInt(goalScore) || 720) },
              warnings: ['Connection issue - showing general recommendations. Re-upload your report for personalized analysis.'],
              suggestions: ['Upload an Experian report for the most accurate analysis']
            };

            if (skipPayment || betaCode) {
              localStorage.setItem('creditSessionId', sessionId);
              navigate('/upsell', { state: { sessionId, fromBeta: !!betaCode, planData: fallbackPlan } });
            } else {
              navigate('/preview', { state: { sessionId, planData: fallbackPlan, reportSource } });
            }
          }
        }, 2000);
      }
    };

    generatePlan();
  }, [sessionId, timeline, goalScore, monthlyBudget, skipPayment, betaCode, reportSource, navigate]);

  const currentProgress = PROGRESS_STAGES[progressIndex] || PROGRESS_STAGES[0];

  return (
    <div className="processing-container">
      <div className="processing-box">
        {/* Progress Animation */}
        {(status === 'generating' || status === 'retrying') && (
          <>
            <div className="processing-spinner" data-testid="loading-spinner">
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
            </div>
            
            <div className="processing-text" data-testid="loading-text">
              {currentProgress.message}
            </div>
            <div className="processing-subtext">
              {currentProgress.subtext}
            </div>
            
            {/* Progress dots */}
            <div className="progress-dots">
              {PROGRESS_STAGES.map((_, idx) => (
                <div 
                  key={idx} 
                  className={`progress-dot ${idx <= progressIndex ? 'active' : ''}`}
                />
              ))}
            </div>
          </>
        )}

        {/* Micro Result Display */}
        {status === 'complete' && microResult && (
          <div className="micro-result" data-testid="micro-result">
            <div className="result-icon">✓</div>
            <h2 className="result-title">Analysis Complete!</h2>
            <p className="result-stat">
              {microResult.pointsAway 
                ? `You're ${microResult.pointsAway} points away from your goal`
                : `We found ${microResult.opportunities} key opportunities to increase your score`}
            </p>
            {microResult.analysisMessage && (
              <p className="result-message" style={{fontSize: '14px', color: '#94a3b8', marginTop: '8px'}}>
                {microResult.analysisMessage}
              </p>
            )}
            {microResult.hasWarning && (
              <p className="result-warning">
                For best accuracy, we recommend uploading an Experian report
              </p>
            )}
          </div>
        )}

        {/* Unlocking State */}
        {status === 'unlocking' && (
          <>
            <div className="processing-spinner">
              <div className="spinner-ring"></div>
            </div>
            <div className="processing-text">Unlocking your plan...</div>
            <div className="processing-subtext">Almost there!</div>
          </>
        )}

        {/* Validation Error */}
        {status === 'validation_error' && (
          <div className="error-state">
            <div className="error-icon">⚠️</div>
            <h2 className="error-title">Credit Report Issue Detected</h2>
            <p className="error-message">{errorMessage}</p>
            <p className="error-hint">
              This can happen if the PDF is blurry, password-protected, or missing key account information.
            </p>
            <div className="error-actions">
              <button 
                onClick={() => navigate('/upload', { state: { isRetry: true } })} 
                className="btn-primary"
                data-testid="reupload-btn"
              >
                Re-upload Credit Report
              </button>
              <button 
                onClick={() => navigate('/')} 
                className="btn-secondary"
              >
                Go Home
              </button>
            </div>
          </div>
        )}

        {/* General Error */}
        {status === 'error' && (
          <div className="error-state">
            <div className="error-icon">❌</div>
            <h2 className="error-title">Having trouble generating your plan</h2>
            <p className="error-message">{errorMessage}</p>
            <div className="error-actions">
              <button 
                onClick={() => window.location.reload()} 
                className="btn-primary"
              >
                Try Again
              </button>
              <button 
                onClick={() => navigate('/')} 
                className="btn-secondary"
              >
                Go Home
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProcessingPage;
